import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Card from '../../components/UI/Card';
import Modal from '../../components/UI/Modal';
import { donationProjectsAPI } from '../../services/api';
import { getStorageUrl } from '../../utils/imageHelpers';
import { Plus, Edit, Trash2, Image as ImageIcon, Save, X, Heart, DollarSign, Building2 } from 'lucide-react';

const Container = styled.div`
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['3xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const ProjectCard = styled(Card)`
  padding: 1.5rem;
  position: relative;
`;

const ProjectImage = styled.div`
  width: 100%;
  height: 180px;
  border-radius: ${props => props.theme.borderRadius.md};
  overflow: hidden;
  margin-bottom: 1rem;
  background-color: ${props => props.theme.colors.background.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ProjectTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 0.5rem 0;
`;

const ProjectDescription = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  margin: 0 0 1rem 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const AmountInfo = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: 0.5rem;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const StatusBadge = styled.span`
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.25rem 0.75rem;
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  background-color: ${props => props.$active ? props.theme.colors.success + '20' : props.theme.colors.error + '20'};
  color: ${props => props.$active ? props.theme.colors.success : props.theme.colors.error};
`;

const FeaturedBadge = styled.span`
  position: absolute;
  top: 1rem;
  left: 1rem;
  padding: 0.25rem 0.75rem;
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  background-color: ${props => props.theme.colors.primary + '20'};
  color: ${props => props.theme.colors.primary};
`;

const ModalContent = styled.div`
  padding: 1.5rem;
  max-height: 80vh;
  overflow-y: auto;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: ${props => props.rows ? props.rows * 1.5 : 4}rem;
  padding: 0.75rem;
  font-size: ${props => props.theme.typography.fontSize.base};
  border: 1px solid ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }
`;

const ImageUploadArea = styled.div`
  border: 2px dashed ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.primary}05;
  }
`;

const PreviewImage = styled.div`
  margin-top: 1rem;
  
  img {
    max-width: 100%;
    max-height: 200px;
    border-radius: ${props => props.theme.borderRadius.md};
  }
`;

const BankAccountCard = styled.div`
  border: 1px solid ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: 1rem;
  margin-bottom: 1rem;
  background-color: ${props => props.theme.colors.background.secondary};
`;

const BankAccountHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DonationProjectsManagement = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    short_description: '',
    target_amount: '',
    current_amount: '0',
    currency: 'ETB',
    bank_accounts: [],
    project_image: null,
    order: 0,
    is_active: true,
    is_featured: false,
    start_date: '',
    end_date: ''
  });
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await donationProjectsAPI.getAll({ all: true });
      setProjects(response.data || []);
    } catch (error) {
      console.error('Error fetching donation projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (project = null) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        title: project.title || '',
        description: project.description || '',
        short_description: project.short_description || '',
        target_amount: project.target_amount || '',
        current_amount: project.current_amount || '0',
        currency: project.currency || 'ETB',
        bank_accounts: project.bank_accounts || [],
        project_image: null,
        order: project.order || 0,
        is_active: project.is_active !== undefined ? project.is_active : true,
        is_featured: project.is_featured !== undefined ? project.is_featured : false,
        start_date: project.start_date || '',
        end_date: project.end_date || ''
      });
      setImagePreview(project.project_image ? getStorageUrl(project.project_image) : null);
    } else {
      setEditingProject(null);
      setFormData({
        title: '',
        description: '',
        short_description: '',
        target_amount: '',
        current_amount: '0',
        currency: 'ETB',
        bank_accounts: [],
        project_image: null,
        order: 0,
        is_active: true,
        is_featured: false,
        start_date: '',
        end_date: ''
      });
      setImagePreview(null);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingProject(null);
    setFormData({
      title: '',
      description: '',
      short_description: '',
      target_amount: '',
      current_amount: '0',
      currency: 'ETB',
      bank_accounts: [],
      project_image: null,
      order: 0,
      is_active: true,
      is_featured: false,
      start_date: '',
      end_date: ''
    });
    setImagePreview(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, project_image: file }));
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBankAccountChange = (index, field, value) => {
    const newAccounts = [...formData.bank_accounts];
    newAccounts[index] = {
      ...newAccounts[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      bank_accounts: newAccounts
    }));
  };

  const addBankAccount = () => {
    setFormData(prev => ({
      ...prev,
      bank_accounts: [...prev.bank_accounts, {
        bank_name: '',
        account_name: '',
        account_number: '',
        swift_code: ''
      }]
    }));
  };

  const removeBankAccount = (index) => {
    setFormData(prev => ({
      ...prev,
      bank_accounts: prev.bank_accounts.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('You are not logged in. Please log in and try again.');
        window.location.href = '/login';
        return;
      }

      console.log('Submitting donation project data:', formData);
      if (editingProject) {
        await donationProjectsAPI.update(editingProject.id, formData);
      } else {
        await donationProjectsAPI.create(formData);
      }
      handleCloseModal();
      fetchProjects();
    } catch (error) {
      console.error('Error saving donation project:', error);
      console.error('Error response:', error.response?.data);
      if (error.response?.status === 401) {
        alert('Your session has expired. Please log in again.');
        window.location.href = '/login';
      } else if (error.response?.status === 422) {
        // Show validation errors
        const errors = error.response?.data?.errors;
        if (errors) {
          const errorMessages = Object.entries(errors)
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('\n');
          alert(`Validation errors:\n${errorMessages}`);
        } else {
          alert('Validation error. Please check all required fields.');
        }
      } else {
        alert('Failed to save donation project. Please try again.');
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this donation project?')) {
      try {
        await donationProjectsAPI.delete(id);
        fetchProjects();
      } catch (error) {
        console.error('Error deleting donation project:', error);
        alert('Failed to delete donation project. Please try again.');
      }
    }
  };

  return (
    <Container>
      <Header>
        <Title>Donation Projects Management</Title>
        <Button variant="primary" onClick={() => handleOpenModal()}>
          <Plus size={20} style={{ marginRight: '0.5rem' }} />
          Add Project
        </Button>
      </Header>

      {loading ? (
        <p>Loading donation projects...</p>
      ) : projects.length === 0 ? (
        <Card style={{ padding: '3rem', textAlign: 'center' }}>
          <p>No donation projects yet. Click "Add Project" to create one.</p>
        </Card>
      ) : (
        <Grid>
          {projects.map((project) => (
            <ProjectCard key={project.id}>
              <StatusBadge $active={project.is_active}>
                {project.is_active ? 'Active' : 'Inactive'}
              </StatusBadge>
              {project.is_featured && (
                <FeaturedBadge>Featured</FeaturedBadge>
              )}
              <ProjectImage>
                {project.project_image ? (
                  <img src={getStorageUrl(project.project_image)} alt={project.title} />
                ) : (
                  <ImageIcon size={48} style={{ opacity: 0.3 }} />
                )}
              </ProjectImage>
              <ProjectTitle>{project.title}</ProjectTitle>
              <ProjectDescription>
                {project.short_description || project.description}
              </ProjectDescription>
              {project.target_amount && (
                <AmountInfo>
                  <span>Target: {new Intl.NumberFormat('en-US', { style: 'currency', currency: project.currency || 'ETB', minimumFractionDigits: 0 }).format(project.target_amount)}</span>
                  <span>Raised: {new Intl.NumberFormat('en-US', { style: 'currency', currency: project.currency || 'ETB', minimumFractionDigits: 0 }).format(project.current_amount || 0)}</span>
                </AmountInfo>
              )}
              <Actions>
                <Button
                  variant="outline"
                  size="small"
                  onClick={() => handleOpenModal(project)}
                >
                  <Edit size={16} />
                </Button>
                <Button
                  variant="outline"
                  size="small"
                  onClick={() => handleDelete(project.id)}
                >
                  <Trash2 size={16} />
                </Button>
              </Actions>
            </ProjectCard>
          ))}
        </Grid>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingProject ? 'Edit Donation Project' : 'Add Donation Project'}
      >
        <ModalContent>
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Project Title *</Label>
              <Input
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder="e.g., Women's Legal Support Program"
              />
            </FormGroup>

            <FormGroup>
              <Label>Short Description</Label>
              <Textarea
                name="short_description"
                value={formData.short_description}
                onChange={handleInputChange}
                rows={2}
                placeholder="Brief description (shown on cards)"
              />
            </FormGroup>

            <FormGroup>
              <Label>Full Description *</Label>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={4}
                required
                placeholder="Detailed description of the project..."
              />
            </FormGroup>

            <Row>
              <FormGroup>
                <Label>Target Amount</Label>
                <Input
                  name="target_amount"
                  type="number"
                  step="0.01"
                  value={formData.target_amount}
                  onChange={handleInputChange}
                  placeholder="0.00"
                />
              </FormGroup>

              <FormGroup>
                <Label>Current Amount</Label>
                <Input
                  name="current_amount"
                  type="number"
                  step="0.01"
                  value={formData.current_amount}
                  onChange={handleInputChange}
                  placeholder="0.00"
                />
              </FormGroup>
            </Row>

            <Row>
              <FormGroup>
                <Label>Currency</Label>
                <Input
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                  placeholder="ETB"
                  maxLength={3}
                />
              </FormGroup>

              <FormGroup>
                <Label>Order</Label>
                <Input
                  name="order"
                  type="number"
                  value={formData.order}
                  onChange={handleInputChange}
                  min="0"
                />
              </FormGroup>
            </Row>

            <Row>
              <FormGroup>
                <Label>Start Date</Label>
                <Input
                  name="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={handleInputChange}
                />
              </FormGroup>

              <FormGroup>
                <Label>End Date</Label>
                <Input
                  name="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={handleInputChange}
                />
              </FormGroup>
            </Row>

            <FormGroup>
              <Label>Project Image</Label>
              <ImageUploadArea onClick={() => document.getElementById('projectImageInput').click()}>
                <ImageIcon size={32} style={{ margin: '0 auto 0.5rem', display: 'block' }} />
                <p>Click to upload image</p>
                <p style={{ fontSize: '0.875rem', opacity: 0.7 }}>JPG, PNG or GIF (Max 5MB)</p>
              </ImageUploadArea>
              <input
                id="projectImageInput"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleImageChange}
              />
              {imagePreview && (
                <PreviewImage>
                  <img src={imagePreview} alt="Preview" />
                </PreviewImage>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Bank Accounts</Label>
              {formData.bank_accounts.map((account, index) => (
                <BankAccountCard key={index}>
                  <BankAccountHeader>
                    <strong>Account {index + 1}</strong>
                    <Button
                      type="button"
                      variant="outline"
                      size="small"
                      onClick={() => removeBankAccount(index)}
                    >
                      <X size={16} />
                    </Button>
                  </BankAccountHeader>
                  <FormGroup>
                    <Label>Bank Name *</Label>
                    <Input
                      value={account.bank_name}
                      onChange={(e) => handleBankAccountChange(index, 'bank_name', e.target.value)}
                      required
                      placeholder="Bank name"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Account Name *</Label>
                    <Input
                      value={account.account_name}
                      onChange={(e) => handleBankAccountChange(index, 'account_name', e.target.value)}
                      required
                      placeholder="Account holder name"
                    />
                  </FormGroup>
                  <Row>
                    <FormGroup>
                      <Label>Account Number *</Label>
                      <Input
                        value={account.account_number}
                        onChange={(e) => handleBankAccountChange(index, 'account_number', e.target.value)}
                        required
                        placeholder="Account number"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>SWIFT Code</Label>
                      <Input
                        value={account.swift_code}
                        onChange={(e) => handleBankAccountChange(index, 'swift_code', e.target.value)}
                        placeholder="SWIFT/BIC code"
                      />
                    </FormGroup>
                  </Row>
                </BankAccountCard>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={addBankAccount}
                style={{ marginTop: '0.5rem' }}
              >
                <Building2 size={16} style={{ marginRight: '0.5rem' }} />
                Add Bank Account
              </Button>
            </FormGroup>

            <FormGroup>
              <Label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                Active (visible on donate page)
              </Label>
            </FormGroup>

            <FormGroup>
              <Label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  name="is_featured"
                  checked={formData.is_featured}
                  onChange={handleInputChange}
                />
                Featured (highlighted on page)
              </Label>
            </FormGroup>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '2rem' }}>
              <Button type="button" variant="outline" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                <Save size={20} style={{ marginRight: '0.5rem' }} />
                {editingProject ? 'Update' : 'Create'} Project
              </Button>
            </div>
          </form>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default DonationProjectsManagement;

